import { create } from 'zustand';
import { normalizePins } from '@/client/api/nav/nav-pins.query';

const STORAGE_KEY = 'ff-admin:nav-pins:v1';

type SidebarNavState = {
  pins: string[];
  hydrated: boolean;
  syncing: boolean;
  lastSyncError: string | null;
  hydrateFromLocal: () => void;
  setPinsFromServer: (pins: string[]) => void;
  setPins: (pins: string[]) => void;
  togglePin: (pinKey: string) => void;
  reorderPins: (fromIndex: number, toIndex: number) => void;
  isPinned: (title: string) => boolean;
  setSyncing: (syncing: boolean) => void;
  setLastSyncError: (error: string | null) => void;
};

const readLocal = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return normalizePins(JSON.parse(raw));
  } catch {
    return [];
  }
};

const writeLocal = (pins: string[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizePins(pins)));
  } catch {
    // quota / private mode — ignore
  }
};

export const useSidebarNavStore = create<SidebarNavState>((set, get) => ({
  pins: [],
  hydrated: false,
  syncing: false,
  lastSyncError: null,

  hydrateFromLocal: () => {
    const pins = readLocal();
    set({ pins, hydrated: true });
  },

  setPinsFromServer: (pins) => {
    const next = normalizePins(pins);
    writeLocal(next);
    set({ pins: next, lastSyncError: null });
  },

  setPins: (pins) => {
    const next = normalizePins(pins);
    writeLocal(next);
    set({ pins: next });
  },

  togglePin: (pinKey) => {
    const t = pinKey.trim();
    if (!t) return;
    const current = get().pins;
    const next = current.includes(t)
      ? current.filter((p) => p !== t)
      : normalizePins([...current, t]);
    writeLocal(next);
    set({ pins: next });
  },

  reorderPins: (fromIndex, toIndex) => {
    const current = [...get().pins];
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= current.length ||
      toIndex >= current.length ||
      fromIndex === toIndex
    ) {
      return;
    }
    const [item] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, item);
    writeLocal(current);
    set({ pins: current });
  },

  isPinned: (title) => get().pins.includes(title),

  setSyncing: (syncing) => set({ syncing }),
  setLastSyncError: (error) => set({ lastSyncError: error }),
}));

export { STORAGE_KEY };
